/**
 * Native push registration (APNs on iOS, FCM on Android).
 *
 * Mounted once inside the authenticated layout (routes/_auth.tsx). When a crew
 * member is signed in on a native device it requests push permission,
 * registers with the platform push service, and upserts the resulting device
 * token (with its platform) so the push action can address them. Tapping a
 * delivered notification deep-links via the same parser the inbox uses.
 *
 * Pushes are delivered for every company the user belongs to, not just the
 * active one. A tap on a cross-company notification surfaces `pendingSwitch`
 * instead of navigating; the authenticated layout renders a confirmation
 * dialog and calls confirm/cancel.
 *
 * No-op on web.
 */

import { useEffect, useRef, useState } from 'react'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import {
  PushNotifications,
  type ActionPerformed,
  type Token,
} from '@capacitor/push-notifications'
import { useRouter } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/capacitor'
import { usePushRegisterToken } from '@/lib/hooks/push'
import { useOrpcClient } from '@/lib/orpc'
import { parseNotificationLink } from '@/lib/notificationLink'
import { getActiveCompanyId, setActiveCompanyId } from '@/lib/company-context'

// A tapped notification that belongs to another company, awaiting the user's
// go-ahead to switch before navigating.
export type PendingCompanySwitch = {
  companyId: string
  companyName: string
  link: string
}

export function usePushRegistration(crewId: string | undefined) {
  const { mutateAsync: registerToken } = usePushRegisterToken()
  const router = useRouter()
  const queryClient = useQueryClient()
  const orpcClient = useOrpcClient()

  // Keep the latest crewId/mutation reachable from listener callbacks without
  // re-subscribing the native listeners on every render.
  const crewIdRef = useRef(crewId)
  crewIdRef.current = crewId
  const registerTokenRef = useRef(registerToken)
  registerTokenRef.current = registerToken
  const orpcClientRef = useRef(orpcClient)
  orpcClientRef.current = orpcClient
  const queryClientRef = useRef(queryClient)
  queryClientRef.current = queryClient

  const [pendingSwitch, setPendingSwitch] = useState<PendingCompanySwitch | null>(
    null
  )

  const navigateToLink = async (link: string) => {
    const { to, search } = parseNotificationLink(link)
    await router.navigate({ to: to as never, search: search as never })
    // A push means something changed server-side, and cached data can be
    // younger than staleTime yet older than that change. Invalidating after
    // navigating refetches only the target page's (active) queries now — also
    // when the tap lands on the route already on screen — and marks the rest
    // stale so they refetch on their next mount.
    void queryClientRef.current.invalidateQueries()
  }

  // A push can target a different company than the active one (each membership
  // is its own crew row). A cross-company tap must not switch silently: verify
  // the user actually belongs to the target company (a stale push for a removed
  // membership must not wedge the app in a 403 loop), then hand off to the
  // confirmation dialog. Returns true when the dialog now owns navigation.
  // Best-effort: any failure falls through to plain navigation.
  const requestSwitchIfNeeded = async (
    targetCompanyId: string,
    targetCompanyName: string | undefined,
    link: string
  ): Promise<boolean> => {
    const activeCompanyId = await getActiveCompanyId()
    if (activeCompanyId === targetCompanyId) return false
    const memberships = await orpcClientRef.current.company.listMine()
    const membership = memberships.find((m) => m.companyId === targetCompanyId)
    if (!membership) return false
    // Single-company users never picked a company (no header sent; the server
    // falls back to their only membership) — nothing to switch.
    if (activeCompanyId === null && memberships.length === 1) return false
    setPendingSwitch({
      companyId: targetCompanyId,
      companyName: targetCompanyName ?? membership.companyName,
      link,
    })
    return true
  }

  const handleNotificationTap = async (action: ActionPerformed) => {
    const data = action.notification.data as
      | { link?: string; companyId?: string; companyName?: string }
      | undefined
    const link = data?.link
    if (!link) return
    const targetCompanyId = data?.companyId
    if (targetCompanyId) {
      try {
        const awaitingConfirmation = await requestSwitchIfNeeded(
          targetCompanyId,
          data?.companyName,
          link
        )
        if (awaitingConfirmation) return
      } catch (error) {
        Sentry.captureException(error)
      }
    }
    await navigateToLink(link)
  }
  const handleNotificationTapRef = useRef(handleNotificationTap)
  handleNotificationTapRef.current = handleNotificationTap

  const confirmPendingSwitch = async () => {
    if (!pendingSwitch) return
    const { companyId, link } = pendingSwitch
    setPendingSwitch(null)
    // Persist the new company first so every refetch triggered by the cache
    // clear already carries the new x-company-id header.
    await setActiveCompanyId(companyId)
    queryClientRef.current.clear()
    await navigateToLink(link)
  }

  const cancelPendingSwitch = () => {
    // No navigation: the link points at an entity the current company can't
    // load anyway.
    setPendingSwitch(null)
  }

  const isNative = Capacitor.isNativePlatform()
  // Android push requires Firebase (google-services.json baked into the APK);
  // without it, native register() crashes the app — skip until it's configured.
  // See VITE_ANDROID_PUSH_READY in vite.config.ts.
  const pushReady =
    Capacitor.getPlatform() !== 'android' ||
    import.meta.env.VITE_ANDROID_PUSH_READY === true

  useEffect(() => {
    if (!isNative || !pushReady || !crewId) return

    let cancelled = false
    const handles: PluginListenerHandle[] = []

    async function setup() {
      const registration = await PushNotifications.addListener(
        'registration',
        (token: Token) => {
          // Only register once we know who's signed in (server derives crewId
          // from auth, but we gate to avoid registering for a signed-out user).
          if (!crewIdRef.current) return
          void registerTokenRef
            .current({ token: token.value, platform: Capacitor.getPlatform() })
            .catch((error: unknown) => Sentry.captureException(error))
        }
      )
      handles.push(registration)

      const registrationError = await PushNotifications.addListener(
        'registrationError',
        (error) => {
          Sentry.captureException(
            new Error(`Push registration error: ${JSON.stringify(error)}`)
          )
        }
      )
      handles.push(registrationError)

      const actionPerformed = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action) => {
          void handleNotificationTapRef
            .current(action)
            .catch((error: unknown) => Sentry.captureException(error))
        }
      )
      handles.push(actionPerformed)

      // If the effect was torn down while listeners were registering, undo them.
      if (cancelled) {
        for (const handle of handles) void handle.remove()
        return
      }

      // Ask for permission (idempotent — the OS only prompts once; on
      // Android 13+ this is the POST_NOTIFICATIONS runtime prompt) and register.
      const current = await PushNotifications.checkPermissions()
      let receive = current.receive
      if (receive === 'prompt' || receive === 'prompt-with-rationale') {
        receive = (await PushNotifications.requestPermissions()).receive
      }
      if (receive === 'granted' && !cancelled) {
        await PushNotifications.register()
      }
    }

    void setup().catch((error: unknown) => Sentry.captureException(error))

    return () => {
      cancelled = true
      for (const handle of handles) void handle.remove()
    }
  }, [isNative, pushReady, crewId, router])

  return { pendingSwitch, confirmPendingSwitch, cancelPendingSwitch }
}
